import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { Permission, PunchStatus, PunchType, PurchaseStatus } from '@construction/shared';
import { api } from '@/api/client';
import { attendanceApi, type Punch } from '@/api/endpoints';
import { invalidateAttendance } from '@/features/attendance/invalidate';
import { Alert, Button, Drawer, EmptyState, ListGroup, ListRow, PageHeader, Spinner } from '@/components/ui';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { useMobileSite } from '@/hooks/useCurrentSite';
import { usePermissions } from '@/lib/permissions';
import { formatMoney } from '@/lib/format';
import { PUNCH_OVERRIDE_KEY, PUNCH_REASON_KEY } from '@/lib/punch-labels';

interface PendingPurchase {
  id: string;
  purchasedAt: string;
  vendor: string | null;
  category: string | null;
  amountCents: number;
  currency: string;
  note: string | null;
  user?: { id: string; employeeNo: string; name: string };
}

type Kind = 'PUNCH' | 'ANOMALY' | 'PURCHASE' | 'REIMBURSE';

interface Item {
  kind: Kind;
  id: string;
  at: string;
  who: string;
  summary: string;
}

/**
 * 审核中心：把待复核打卡和待审采购合在一个列表里。
 *
 * 超管在工地上最常做的就是「有什么要我批的」。原先这几类分散在各自的页面，
 * 得分别点进去才知道有没有东西 —— 网页端有左侧导航还能忍，
 * 手机上就意味着每次都要翻几遍。
 *
 * 合并只在展示层做，审批仍然各走各的接口：两种业务的状态机不一样
 * （采购有「已报销」终态，打卡有距离和模拟定位要看），硬做成一个通用接口
 * 会把差异都埋进参数里。
 *
 * **考勤异常是单独一类**（CR-052）：不限日期、在岗不算。补录要填表，所以卡片上只有「去处理」，落在考勤页的「异常」页签。
 *
 * **待报销是单独一类**（CR-049）：审批 → 报销两步走的第二步（Zoho Expense 的「待报销」页签），
 * 卡片上只有「标记已报销」—— 驳回已批准的是少见的回头路，去详情页做。三类都不限日期。
 *
 * **日报那一类 CR-023 撤了** —— 日报不再有审核流，它是流水记录，
 * 留在这里只会让人以为自己漏批了什么。
 *
 * CR-082 按示例重做：四类各一组（组名带数），点一条从底部弹出详情，通过 / 批准在详情底部。
 * 改之前是一张一张卡片、每张卡片下面一排按钮，外加一排按类型筛的胶囊（分组之后用不着了）。
 */
export function ReviewPage() {
  const { t, i18n } = useTranslation();
  const toMessage = useErrorMessage();
  const queryClient = useQueryClient();
  const { site, siteId, isLoading } = useMobileSite();
  // 两种复核是两个勾（CR-038）：只有一种的人不该看到另一种的 403
  const { can } = usePermissions();
  const canPunches = can(Permission.ATTENDANCE_MANAGE);
  const canPurchases = can(Permission.PURCHASE_REVIEW);
  // 类型筛选只列这个人能处理的；只剩一种时整排不出现（CR-039）
  const kinds = (['PUNCH', 'ANOMALY', 'PURCHASE', 'REIMBURSE'] as const).filter((kind) =>
    kind === 'PUNCH' || kind === 'ANOMALY' ? canPunches : canPurchases,
  );
  const [open, setOpen] = useState<Item | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [punches, purchases, reimbursements, anomalies] = useQueries({
    queries: [
      {
        queryKey: ['review-punches', siteId],
        queryFn: () => attendanceApi.list(siteId!, { status: PunchStatus.PENDING_REVIEW }),
        enabled: siteId !== null && canPunches,
      },
      {
        queryKey: ['review-purchases', siteId],
        queryFn: () =>
          api.get<PendingPurchase[]>(`/sites/${siteId}/purchases`, {
            status: PurchaseStatus.PENDING,
          }),
        enabled: siteId !== null && canPurchases,
      },
      {
        queryKey: ['review-reimbursements', siteId],
        queryFn: () =>
          api.get<PendingPurchase[]>(`/sites/${siteId}/purchases`, {
            status: PurchaseStatus.APPROVED,
          }),
        enabled: siteId !== null && canPurchases,
      },
      {
        queryKey: ['review-anomalies', siteId],
        queryFn: () => attendanceApi.exceptions(siteId!),
        enabled: siteId !== null && canPunches,
      },
    ],
  });

  const items = useMemo<Item[]>(() => {
    const purchaseItem = (kind: 'PURCHASE' | 'REIMBURSE', p: PendingPurchase): Item => ({
      kind,
      id: p.id,
      at: p.purchasedAt,
      who: p.user?.name ?? '—',
      summary: [formatMoney(p.amountCents, i18n.language, p.currency), p.vendor, p.category]
        .filter(Boolean)
        .join(' · '),
    });

    const all: Item[] = [
      ...((punches.data ?? []) as Punch[]).map((p) => ({
        kind: 'PUNCH' as const,
        id: p.id,
        at: p.occurredAt,
        who: p.user?.name ?? `#${p.userId}`,
        summary: [
          t(p.type === PunchType.IN ? 'punch.clockIn' : 'punch.clockOut'),
          p.reason && PUNCH_REASON_KEY[p.reason] ? t(PUNCH_REASON_KEY[p.reason]!) : p.reason,
          // 没坐标就没距离。写 `${null}m` 会渲染出 "nullm"，Math.round(null) 是 0 —— 两种都在骗人
          p.distanceM == null ? t('attendance.noLocation') : `${Math.round(p.distanceM)}m`,
          // 工人自己填的理由：驳回前管理者最需要看的就是这个。
          // 自写内容原样显示不翻译（CLAUDE.md：用户输入不翻译）
          p.overrideReason && PUNCH_OVERRIDE_KEY[p.overrideReason]
            ? t(PUNCH_OVERRIDE_KEY[p.overrideReason]!)
            : null,
          p.overrideReasonNote,
        ]
          .filter(Boolean)
          .join(' · '),
      })),
      ...(anomalies.data?.anomalies ?? []).map((a) => ({
        kind: 'ANOMALY' as const,
        id: a.punch.id,
        at: a.punch.occurredAt,
        who: a.punch.user?.name ?? `#${a.punch.userId}`,
        summary: t(`attendance.anomaly.${a.reason}`),
      })),
      ...(purchases.data ?? []).map((p) => purchaseItem('PURCHASE', p)),
      ...(reimbursements.data ?? []).map((p) => purchaseItem('REIMBURSE', p)),
    ];

    // 按时间倒序：最近发生的先处理
    all.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
    return all;
  }, [punches.data, anomalies.data, purchases.data, reimbursements.data, t, i18n.language]);

  const review = useMutation({
    mutationFn: async ({ item, approve }: { item: Item; approve: boolean }) => {
      // 两类的状态机不一样，各走各的接口
      if (item.kind === 'PUNCH') {
        return attendanceApi.review(
          item.id,
          approve ? PunchStatus.APPROVED : PunchStatus.REJECTED,
        );
      }
      if (item.kind === 'REIMBURSE') {
        return api.post(`/purchases/${item.id}/review`, { status: PurchaseStatus.REIMBURSED });
      }
      return api.post(`/purchases/${item.id}/review`, {
        status: approve ? PurchaseStatus.APPROVED : PurchaseStatus.REJECTED,
      });
    },
    onSuccess: () => {
      invalidateAttendance(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['review-purchases', siteId] });
      void queryClient.invalidateQueries({ queryKey: ['review-reimbursements', siteId] });
      void queryClient.invalidateQueries({ queryKey: ['overview', siteId] });
      // 批过、报过的采购在采购页也要跟着变
      void queryClient.invalidateQueries({ queryKey: ['purchases'] });
      void queryClient.invalidateQueries({ queryKey: ['purchase-summary'] });
      void queryClient.invalidateQueries({ queryKey: ['my-purchases'] });
      setError(null);
      setOpen(null);
    },
    onError: (err) => setError(toMessage(err)),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-20 text-ink-3">
        <Spinner />
      </div>
    );
  }

  if (!site) {
    return <EmptyState title={t('site.empty')} hint={t('site.emptyHintManager')} />;
  }

  const loading = punches.isLoading || purchases.isLoading || reimbursements.isLoading || anomalies.isLoading;
  const when = (at: string) =>
    new Date(at).toLocaleString(i18n.language, {
      timeZone: site.timezone,
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  // 驳回前常常要看照片、看小票，细节留在各自的页面里
  // 采购的详情去采购页的「本工地」tab（CR-042：一页两 tab，`?tab=site` 直接落在那一边）
  const pageFor = (kind: Kind) =>
    kind === 'PUNCH'
      ? '/m/attendance?view=pending'
      : kind === 'ANOMALY'
        ? '/m/attendance?view=anomalies'
        : `/m/purchases?tab=site&status=${kind === 'REIMBURSE' ? PurchaseStatus.APPROVED : PurchaseStatus.PENDING}`;

  return (
    <>
      <PageHeader title={t('review.title')} description={site.name} />

      {error && (
        <div className="mb-3">
          <Alert>{error}</Alert>
        </div>
      )}

      {loading && (
        <div className="flex justify-center py-14 text-ink-3">
          <Spinner />
        </div>
      )}

      {!loading && items.length === 0 && <EmptyState title={t('review.empty')} hint={t('review.emptyHint')} />}

      <div className="flex flex-col gap-5">
        {kinds.map((kind) => {
          const list = items.filter((item) => item.kind === kind);
          if (list.length === 0) return null;
          return (
            <ListGroup
              key={kind}
              title={`${t(`review.type${titleCase(kind)}`)} ${list.length}`}
              action={
                <Link
                  to={pageFor(kind)}
                  data-entry={kind === 'PUNCH' || kind === 'ANOMALY' ? 'm.attendance' : 'm.purchases'}
                  className="font-medium text-brand-600"
                >
                  {kind === 'PUNCH' || kind === 'ANOMALY' ? t('nav.attendance') : t('nav.purchases')}
                </Link>
              }
            >
              {list.map((item) => (
                <ListRow
                  key={item.id}
                  onClick={() => setOpen(item)}
                  title={item.who}
                  subtitle={item.summary}
                  trailing={<span className="tabular-nums">{when(item.at)}</span>}
                />
              ))}
            </ListGroup>
          );
        })}
      </div>

      <Drawer
        open={open !== null}
        onClose={() => setOpen(null)}
        title={open ? `${open.who} · ${t(`review.type${titleCase(open.kind)}`)}` : ''}
        subtitle={open ? when(open.at) : undefined}
        footer={
          open &&
          (open.kind === 'ANOMALY' ? (
            <Link to={pageFor(open.kind)} data-entry="m.attendance" className="w-full">
              <Button block>{t('review.goHandle')}</Button>
            </Link>
          ) : (
            <>
              {open.kind !== 'REIMBURSE' && (
                <Button variant="dangerOutline" onClick={() => review.mutate({ item: open, approve: false })}>
                  {open.kind === 'PUNCH' ? t('attendance.reject') : t('purchases.reject')}
                </Button>
              )}
              <Button
                onClick={() => review.mutate({ item: open, approve: true })}
                loading={review.isPending && review.variables?.item.id === open.id}
              >
                {/* 和考勤页、采购页一个说法（CR-041）：打卡「通过 / 拒绝」，采购「批准 / 驳回」 */}
                {open.kind === 'PUNCH'
                  ? t('attendance.approve')
                  : open.kind === 'REIMBURSE'
                    ? t('purchases.markReimbursed')
                    : t('purchases.approve')}
              </Button>
            </>
          ))
        }
      >
        {open && (
          <div className="flex flex-col gap-4">
            <p className="whitespace-pre-line break-words text-title leading-relaxed text-ink">{open.summary}</p>
            {open.kind !== 'ANOMALY' && (
              <Link
                to={pageFor(open.kind)}
                data-entry={open.kind === 'PUNCH' ? 'm.attendance' : 'm.purchases'}
                className="text-body font-medium text-brand-600"
              >
                {t('review.openDetail')}
              </Link>
            )}
          </div>
        )}
      </Drawer>
    </>
  );
}

function titleCase(kind: Kind): string {
  return kind.charAt(0) + kind.slice(1).toLowerCase();
}
